/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import QRCode from 'qrcode';
import {generate2FaToken, validate2FaToken} from "../../../util/requestSender";
import {reportError} from "../../../util/sentryUtil";
import {createErrorMessage, getFingerprint} from "../../../util/utils";
import User from "../../../models/userModel";
import EnableTwoFactor from "./enableTwoFactor";
import {onShowConfirmSnackbar} from "../../../util/renderUtil";
import {ActivateHandle} from "../../../util/interfaces";


interface Props {
    onSuccess: (user: User) => void;
}

function EnabledTwoFactorController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSuccess} = props;
    const [qrCode, setQrCode] = useState('');
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);

    function onActivate() {
        setActive(true);
        window.setTimeout(loadQr);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setQrCode('');
        setToken('');
    }

    function onChangeToken(token: string) {
        setToken(token);
        if (token.length === 6) onSubmit(token);
    }

    function onSubmit(_token?: string | React.MouseEvent | React.KeyboardEvent) {
        setLoading(true);
        const twoFactorToken: string = typeof _token === 'string' ? _token : token;
        getFingerprint().then((fingerprint: string) => {
            validate2FaToken({
                data: {token: twoFactorToken, two_factor_id: null, fp: fingerprint}, callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        onSuccess(new User(data.user));
                        onClose();
                        onShowConfirmSnackbar('Tweestapsverificatie is ingeschakeld');
                    } else
                        showDialog(createErrorMessage(data.message ?? 'Het valideren van de code is niet gelukt.'),
                            'Validatie mislukt');
                }
            });
        })
    }

    function loadQr() {
        generate2FaToken({
            callback: (data: ResponseData) => {
                if (data.success) {
                    const {qr_code} = data;
                    QRCode.toDataURL(qr_code).then((dataURL: string) => {
                        setQrCode(dataURL);
                    }).catch((err: Error) => {
                        reportError(err);
                        showDialog(createErrorMessage('Het genereren van je persoonlijke code is niet gelukt.'),
                            'Code genereren mislukt', [{
                                label: 'Oké',
                                onClick: onClose
                            }]);
                    })
                } else
                    showDialog(createErrorMessage('Het genereren van je persoonlijke code is niet gelukt.'),
                        'Code genereren mislukt', [{
                            label: 'Oké',
                            onClick: onClose
                        }]);
            }
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <EnableTwoFactor onChange={onChangeToken} onSubmit={onSubmit} onClose={onClose} qrCode={qrCode} token={token}
                         active={active} loading={loading}/>
    )
}

export default forwardRef<ActivateHandle, Props>(EnabledTwoFactorController);